import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Banner } from "@/components/ui/banner";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { InstallGate } from "@/components/install-gate/InstallGate";
import {
  ClientNameReveal,
  gdprErasedTitle,
} from "@/components/clients/ClientNameReveal";
import { useListClients } from "@/hooks/queries/useListClients";
import {
  isSubscriptionLapsed,
  useSubscriptionState,
} from "@/hooks/queries/useSubscriptionState";
import { useStartEvaluation, type StartEvaluationFlow } from "@/hooks/useStartEvaluation";
import { lapsedWriteTooltip } from "@/lib/subscription-copy";
import type { ClientRecord } from "@/hooks/queries/client-types";
import { toListClientsErrorMessage } from "@/routes/clients/client-error";
import { isOfflineError } from "@/lib/api-client";
import { OfflineListNotice } from "@/components/pwa/OfflineListNotice";
import { formatShortDate } from "@/lib/format-date";

// Clients list (UX Screen 11, Story 5.7) — the first feature screen. Renders the
// backend-truthful subset of the mockup: a masked-name + reveal-on-demand column,
// the system Client ID, the Created date, and the "Total clients" KPI. Deferred
// mockup affordances (the "New eval" action, the Recently-active / Requires-follow-up
// filters, lapsed-subscription greying) are either omitted or rendered visibly inert
// with a reason — never faked (see the story's Scope Decisions table + AC6).
// Search is client-side over the loaded page only: by client UUID and by any revealed
// name (server-side name search is impossible — names are encrypted at rest).
// Tokens only; no pure-white background, no hardcoded hex.

const PAGE_LIMIT = 25;

export function ClientsListScreen() {
  const [page, setPage] = React.useState(1);
  const [includeArchived, setIncludeArchived] = React.useState(false);

  const query = useListClients({ page, limit: PAGE_LIMIT, includeArchived });
  const { data, isPending, isError, refetch, isFetching } = query;

  // Story 6.9 — this screen doubles as the "New evaluation" client picker. The header
  // "New evaluation" CTA on Screens 02/12 lands here with `?intent=new-eval`; the
  // per-row "New eval" chip starts the gated create flow for that client.
  const [searchParams] = useSearchParams();
  const newEvalIntent = searchParams.get("intent") === "new-eval";
  const startFlow = useStartEvaluation();

  const [search, setSearch] = React.useState("");
  const [revealedNames, setRevealedNames] = React.useState<Record<string, string>>({});

  const count = data?.count ?? 0;
  const totalPages = count > 0 ? Math.ceil(count / PAGE_LIMIT) : 1;
  const clients = data?.results ?? [];

  // Client-side search over the loaded page: by client UUID and by any revealed name.
  const searchQuery = search.trim().toLowerCase();
  const filteredClients = searchQuery
    ? clients.filter((client) => {
        if (client.id.toLowerCase().includes(searchQuery)) return true;
        const name = revealedNames[client.id];
        return name ? name.toLowerCase().includes(searchQuery) : false;
      })
    : clients;

  const onRevealed = React.useCallback(
    (clientId: string, name: { first: string; last: string }) => {
      setRevealedNames((prev) => ({ ...prev, [clientId]: `${name.first} ${name.last}` }));
    },
    [],
  );

  // gh-471 — offline the clients list can't load (online-only by spec, no IDB cache). Distinguish a
  // genuine offline transport failure from a real server error so the body can show a calm offline
  // notice instead of hanging on "Loading clients…" or flashing the red "couldn't load" banner.
  const offline = isOfflineError(query.error);

  // Clamp the page into range once real data lands — guards against sitting on a
  // phantom page past the end after the result set shrinks (e.g. an archive
  // elsewhere), which would otherwise render an empty page as if the account had
  // no clients. Only acts on settled data, so it never fights an in-flight fetch.
  React.useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  const changeFilter = (next: boolean) => {
    setIncludeArchived(next);
    setPage(1);
    setSearch("");
  };

  // On a disallowed surface, a "New eval" chip click raises the install gate in place of
  // the list (Story 6.9 enforcement). The gate is enforcing (gh-469): no "Continue anyway"
  // escape — the only forward path is installing the PWA, so nothing is created here.
  if (startFlow.gateSurface) {
    return <InstallGate surface={startFlow.gateSurface} />;
  }

  return (
    <section className="flex flex-col gap-6">
      {/* Page header + the single primary CTA for this view. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="display-xs text-[color:var(--text-primary)]">Client records</h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            Find an existing client or onboard a new one. Names stay hidden until you reveal them.
          </p>
        </div>
        <NewClientButton />
      </header>

      {/* New-evaluation picker hint (arrived via the Screen 02/12 "New evaluation" CTA). */}
      {newEvalIntent ? (
        <Banner variant="info" title="Start a new evaluation">
          Choose a client below and select <strong>New eval</strong> to begin. Need a new
          client first? Use <strong>New client</strong> above.
        </Banner>
      ) : null}

      {/* A failed create surfaces here (offline-first create rarely fails, but never silently). */}
      {startFlow.error ? (
        <Banner variant="danger" title="Couldn't start the evaluation">
          {startFlow.error}
        </Banner>
      ) : null}

      {/* KPI row. Only "Total clients" is backend-truthful; per-period counts are
          deferred (a single page's created_at cannot derive them). */}
      <div className="flex flex-wrap gap-4">
        <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-5 py-4">
          <div className="text-sm text-[color:var(--text-tertiary)]">Total clients</div>
          <div className="tabular-nums text-2xl font-semibold text-[color:var(--text-primary)]">
            {data ? count : "—"}
          </div>
        </div>
      </div>

      {/* Controls: a functional Active/Archived filter + client-side search (by client
          UUID and any revealed name; server-side name search is impossible — names are
          encrypted at rest). */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div
          className="inline-flex overflow-hidden rounded-md border border-[color:var(--border-primary)]"
          role="group"
          aria-label="Filter clients by status"
        >
          <FilterButton active={!includeArchived} onClick={() => changeFilter(false)}>
            Active
          </FilterButton>
          <FilterButton active={includeArchived} onClick={() => changeFilter(true)}>
            All clients
          </FilterButton>
        </div>

        <div className="max-w-[420px] w-full">
          <FormField
            label="Search by client / client ID"
            htmlFor="client-search"
            helper="Searches the current page by client ID and any names you've revealed."
          >
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--text-placeholder)]"
              />
              <Input
                id="client-search"
                type="search"
                placeholder="Search by client / client ID"
                className="pl-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </FormField>
        </div>
      </div>

      {/* Error banner (kept above the table so prior data stays visible on a
          background-refetch failure). Suppressed when the failure is just offline — that shows the
          calm offline notice in the body instead of a red alarm (gh-471). */}
      {isError && !offline ? (
        <Banner variant="danger" title="Couldn't load clients">
          <div className="flex flex-col gap-2">
            <span>{toListClientsErrorMessage()}</span>
            <span>
              <Button variant="secondary" size="sm" onClick={() => void refetch()}>
                Try again
              </Button>
            </span>
          </div>
        </Banner>
      ) : null}

      {/* Body: loading / offline / empty / table. role="status" (polite live region) so AT
          announces the loading state (same treatment as ResourcesScreen); aria-busy marks the
          region as updating. */}
      {isPending ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          Loading clients…
        </div>
      ) : offline ? (
        // Offline (transport failure) always shows the honest notice — never an infinite spinner,
        // never "No clients yet" (which would falsely imply the account is empty). When a prior
        // online render left stale rows in cache (`keepPreviousData`), keep them visible UNDER the
        // notice with a "showing last loaded data" caveat rather than blanking them silently — a
        // clinician must never read stale clients as current with no offline signal. gh-471 point 4.
        clients.length > 0 ? (
          <>
            <OfflineListNotice message="You're offline — showing the last loaded client list." />
            <ClientsTable clients={clients} onRevealed={onRevealed} startFlow={startFlow} />
          </>
        ) : (
          <OfflineListNotice />
        )
      ) : count === 0 && !isError ? (
        <EmptyState />
      ) : clients.length > 0 ? (
        <>
          {filteredClients.length === 0 ? (
            <div className="flex items-center justify-center rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-sm text-[color:var(--text-tertiary)]">
              No clients match your search.
            </div>
          ) : (
            <ClientsTable clients={filteredClients} onRevealed={onRevealed} startFlow={startFlow} />
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            hasPrevious={Boolean(data?.previous)}
            hasNext={Boolean(data?.next)}
            isFetching={isFetching}
            onPrevious={() => { setPage((p) => Math.max(1, p - 1)); setSearch(""); }}
            onNext={() => { setPage((p) => p + 1); setSearch(""); }}
          />
        </>
      ) : null}
    </section>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  // Built on the shadcn Button primitive (project constraint: primitives first).
  // `primary`/`ghost` map onto the active/inactive segments; `rounded-none` lets the
  // group's `overflow-hidden rounded-md` own the outer corners, and the inset, zero-
  // offset focus ring stays within the segmented control instead of poking outside it.
  return (
    <Button
      type="button"
      variant={active ? "primary" : "ghost"}
      size="sm"
      onClick={onClick}
      aria-pressed={active}
      className="rounded-none focus-visible:ring-inset focus-visible:ring-offset-0"
    >
      {children}
    </Button>
  );
}

// Story 13.6 AC7 (FR50) — creating a client is a gated WRITE: when the
// subscription is lapsed the CTA renders disabled with a reason (the
// NewEvaluationButton pattern), never a live link to a form whose submit the
// backend would 403 (BILL-001). Loading/error/offline NEVER read as lapsed
// (isSubscriptionLapsed's fail-open display posture — the backend gate is the
// enforcement). Used by both the header and the empty state.
function NewClientButton() {
  const subscription = useSubscriptionState();
  const lapsed = isSubscriptionLapsed(subscription);

  if (lapsed) {
    return (
      <Button
        variant="primary"
        disabled
        aria-disabled="true"
        title="Your access is read-only while the subscription is inactive"
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New client
      </Button>
    );
  }

  return (
    <Button asChild variant="primary">
      <Link to="/clients/new">
        <Plus aria-hidden="true" className="h-4 w-4" />
        New client
      </Link>
    </Button>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] px-6 py-12 text-center">
      <p className="text-md text-[color:var(--text-secondary)]">
        No clients yet — create your first client record.
      </p>
      <NewClientButton />
    </div>
  );
}

function ClientsTable({
  clients,
  onRevealed,
  startFlow,
}: {
  clients: ClientRecord[];
  onRevealed: (clientId: string, name: { first: string; last: string }) => void;
  startFlow: StartEvaluationFlow;
}) {
  return (
    <div className="overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">Your clients</caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className="px-4 py-3 font-semibold">
              Client
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Client ID
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Created
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client) => (
            <ClientRow key={client.id} client={client} onRevealed={onRevealed} startFlow={startFlow} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientRow({
  client,
  onRevealed,
  startFlow,
}: {
  client: ClientRecord;
  onRevealed: (clientId: string, name: { first: string; last: string }) => void;
  startFlow: StartEvaluationFlow;
}) {
  // spec-gh-376: a row erased mid-session (after the list loaded) surfaces only via a
  // 410/ADMIN-004 on Reveal — `ClientNameReveal` latches that internally and notifies
  // here so this row's chip/badge match the terminal state.
  const [erasedByReveal, setErasedByReveal] = React.useState(false);

  const isErased = Boolean(client.gdpr_erased_at) || erasedByReveal;
  const isArchived = Boolean(client.deactivated_at);
  // Erasure is terminal: its rendering takes precedence over the (muted) archived
  // state. Precedence is enforced by the render order (every `isErased` branch is
  // checked before `isArchived`), not by narrowing `isArchived` itself — so the flag
  // keeps meaning literally "is archived".
  const erasedTitle = gdprErasedTitle(client.gdpr_erased_at);

  return (
    <tr className="border-b border-[color:var(--bg-quaternary)] last:border-b-0 align-top">
      <td className="px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <ClientNameReveal
            clientId={client.id}
            initialErased={Boolean(client.gdpr_erased_at)}
            erasedAt={client.gdpr_erased_at}
            onErasedChange={setErasedByReveal}
            onRevealed={(name) => onRevealed(client.id, name)}
          />
          {/* Story 6.9 — the per-row "New eval" action starts the gated create flow for
              this client. Suppressed for erased rows (an erased client can never be
              evaluated); disabled with a reason for archived rows (a soft-deleted client
              shouldn't host a new evaluation). */}
          {!isErased ? (
            <NewEvalChip clientId={client.id} archived={isArchived} startFlow={startFlow} />
          ) : null}
          {isErased ? (
            // Neutral/desaturated terminal chip — deliberately NOT the amber "Archived"
            // token and NOT error-red (a settled state, not an alarm). Erased > Archived.
            <span
              className="inline-flex items-center rounded-full bg-[color:var(--bg-quaternary)] px-3 py-1 text-xs font-semibold text-[color:var(--text-secondary)]"
              title={erasedTitle}
            >
              Erased
            </span>
          ) : isArchived ? (
            <span className="inline-flex items-center rounded-full bg-[color:var(--bg-warning-primary)] px-3 py-1 text-xs font-semibold text-[color:var(--text-warning)]">
              Archived
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-4 py-3 align-middle">
        <span className="tabular-nums text-xs text-[color:var(--text-tertiary)]" title={client.id}>
          {client.id}
        </span>
      </td>
      <td className="px-4 py-3 align-middle tabular-nums text-[color:var(--text-secondary)]">
        {formatShortDate(client.created_at)}
      </td>
    </tr>
  );
}

/**
 * The per-row "New eval" action (Story 6.9). Wired to the shared create flow: clicking
 * it starts a draft for THIS client and navigates to the stepper — unless the client is
 * archived (a soft-deleted client can't host a new evaluation), the subscription is
 * lapsed (disabled, read-only), or the surface is disallowed (the parent screen swaps to
 * the Install Gate). A real button now (no longer the inert Epic-6 placeholder), but kept
 * chip-shaped to match Screen 11.
 */
function NewEvalChip({
  clientId,
  archived,
  startFlow,
}: {
  clientId: string;
  archived: boolean;
  startFlow: StartEvaluationFlow;
}) {
  const disabled = archived || startFlow.lapsed || startFlow.isCreating;
  // gh-568 — the lapse reason is SOURCE-AWARE: an org-seat clinician cannot
  // self-renew, so they are pointed at the organization administrator.
  const title = archived
    ? "Archived clients can't start new evaluations"
    : startFlow.lapsed
      ? lapsedWriteTooltip(
          startFlow.subscription,
          "Renew the subscription to start a new evaluation",
        )
      : "Start a new evaluation for this client";

  return (
    <button
      type="button"
      onClick={() => startFlow.startEvaluation(clientId)}
      disabled={disabled}
      aria-busy={startFlow.isCreating}
      title={title}
      className="inline-flex select-none items-center rounded-full border border-[color:var(--border-brand-alt)] bg-[color:var(--bg-brand-primary)] px-3 py-1 text-xs font-semibold text-[color:var(--text-brand-secondary)] transition-colors hover:bg-[color:var(--bg-brand-secondary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)] disabled:cursor-not-allowed disabled:opacity-50"
    >
      New eval
    </button>
  );
}

function Pagination({
  page,
  totalPages,
  hasPrevious,
  hasNext,
  isFetching,
  onPrevious,
  onNext,
}: {
  page: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  isFetching: boolean;
  onPrevious: () => void;
  onNext: () => void;
}) {
  return (
    <nav className="flex items-center justify-between gap-4" aria-label="Pagination">
      <span className="tabular-nums text-sm text-[color:var(--text-tertiary)]">
        Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onPrevious}
          disabled={!hasPrevious || isFetching}
        >
          <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          Previous
        </Button>
        <Button variant="secondary" size="sm" onClick={onNext} disabled={!hasNext || isFetching}>
          Next
          <ChevronRight aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </nav>
  );
}

export default ClientsListScreen;
